import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import autoTable, { Styles } from 'jspdf-autotable';
import jsPDF from 'jspdf';
import { IPrintData, IPrintDataColumn, IPrintDataSection } from 'src/app/share/services/print.service';
import { IProcessResult } from 'src/app/share/entities/view-model';

import * as moment from 'moment';
import { IDisplaySelectViewModel } from 'src/app/share/forms/form-select/form-select.component';
import { Observable, of } from 'rxjs';
import { delay, switchMap } from 'rxjs/operators';
import { IActionModel } from 'src/app/share/entities/data-view';
import { IDriverBreakDay } from './payroll-break/payroll-break.component';


@Injectable({
  providedIn: 'root'
})
export class PayrollService {

  constructor() { }

  lineCount: number = 0;
  docHeight: number = 0;
  docWidth: number = 0;
  buttomMargin: number = 20;
  xStartPos = 8;
  lineSpace = 5;
  fontName: string = 'helvetica';//'Courier';
  timeSheetFontColor: string = 'helvetica';
  columnXPos: number[] = [];
  pageCount: number = 1;
  dateArray: string[] = [];
  doc: jsPDF;

  ///////////////////////////////////////////////////////// START PDF /////////////////////////////////////////////////////////////////

  /**
   * 
   * @param viewOnly 
   * @param data 
   * @param outputFile 
   * @returns 
   */
  generatePdfFile(data: IPayrollDayData[], outputFile: string, includeTimeSheetPage: boolean, payrollPeriod: IPayrollPeriod, returnBlob: boolean = false, finalVersion: boolean = false ): Blob {
    this.doc = new jsPDF('portrait');

    var message = '';
    var blob: Blob = undefined;

    this.docHeight = this.doc.internal.pageSize.height;
    this.docWidth = this.doc.internal.pageSize.width;

    const timeElapsed = Date.now();
    const today = new Date(timeElapsed);

    this.columnXPos = [this.xStartPos, 28, 56, 106, 121, 145, 162, 186, 201];

    this.dateArray = this.dateRange(payrollPeriod.startDate, payrollPeriod.endDate);

    this.generatePDFData(today, data, includeTimeSheetPage, payrollPeriod, finalVersion);

    if (returnBlob)
      blob = this.doc.output('blob');
    else if (outputFile?.length > 0)
      this.doc.save(outputFile);
    else
      this.doc.output('dataurlnewwindow');

    return blob;
  }

  /**
   * 
   * @param today 
   * @param data 
   * @param includeTimeSheetPage 
   * @param payrollPeriod 
   */
  private generatePDFData(today: Date, data: IPayrollDayData[], includeTimeSheetPage: boolean, payrollPeriod: IPayrollPeriod, finalVersion: boolean) {
    let yPos = 0;

    var driverTotaPrinted: boolean = true;
    var driverNumber: string = '';
    var payrollDayDetail: IPayrollDayDetail[] = [];
    var header: IPayrollDayHeader = {};
    data.forEach((d, idx) => {
      if (driverNumber != d.header.driverNumber) {
        if (driverNumber.length > 0) {
          this.addPeriodTotals(today, header, payrollDayDetail, yPos, payrollPeriod, finalVersion);
          if (includeTimeSheetPage)
            this.createTimeSheetPage(header, payrollDayDetail);
          this.doc.addPage();
          driverTotaPrinted = true;
        }
        header = d.header;
        this.pageCount = 1;
        yPos = this.generatePDFHeader(today, header, payrollPeriod, finalVersion);
        driverNumber = d.header.driverNumber;
        payrollDayDetail = d.payrollDaysData;
      }
      d.payrollDaysData.forEach(dd => {
        dd.activities.forEach(a => {
          if (a.recordType == PayrollRecordType.endOfShift && dd.totals.unengagedMinutes > 0) {
            if ((payrollPeriod.payrollLocation == 'LAX' && payrollPeriod.versionNumber < 5) || payrollPeriod.payrollLocation != 'LAX')
              yPos += this.addUnengagedLine(today, header, yPos, dd.totals.unengagedMinutes, payrollPeriod, finalVersion);
          }
          yPos += this.addActivityLineToPDFReport(today, a, yPos, header, payrollPeriod, finalVersion);
          yPos = this.testForEndOfPage(today, header, yPos, payrollPeriod, finalVersion);
        })
        if (payrollPeriod.payrollLocation == 'LAX' && dd.totals.unpaidBreakMinutes > 0) {
          yPos += this.addMealBreakLine(today, header, yPos, dd.totals.unpaidBreakMinutes, payrollPeriod, finalVersion);
        }
        if (payrollPeriod.payrollLocation == 'LAX' && dd.totals.penaltyBreakMinutes > 0) {
          yPos += this.addPenaltyBreakLine(today, header, yPos, dd.totals.penaltyBreakMinutes, payrollPeriod, finalVersion);
        }
        if (payrollPeriod.payrollLocation == 'LAX' && dd.totals.restPenaltyBreakMinutes > 0 && payrollPeriod.versionNumber >= 5) {
          yPos += this.addRestPenaltyBreakLine(today, header, yPos, dd.totals.restPenaltyBreakMinutes, payrollPeriod, finalVersion);
        }
        dd.dayAdjustment.adjustments.forEach(adj => {
          if (adj.code != 'break-start-time-1' && adj.code != 'break-end-time-1' && adj.code != 'break-start-time-2' && adj.code != 'break-end-time-2') {
            yPos += this.addAdjustmentLineToPDFReport(today, adj, yPos, header, payrollPeriod, finalVersion);
            yPos = this.testForEndOfPage(today, header, yPos, payrollPeriod, finalVersion);
          }
        });
        yPos = this.addDayTotals(today, header, dd.totals, yPos, payrollPeriod, finalVersion);
        driverTotaPrinted = false;
      });
    });

    if (!driverTotaPrinted) {
      this.addPeriodTotals(today, header, payrollDayDetail, yPos, payrollPeriod, finalVersion);
      if (includeTimeSheetPage)
        this.createTimeSheetPage(header, payrollDayDetail);
    }
  }

  generatePDFHeader(today: Date, h: IPayrollDayHeader, payrollPeriod: IPayrollPeriod, finalVersion: boolean): number {

    let y: number = this.pageCount <= 1 ? 50 : 15;
    this.doc.setFontSize(10).setFont(this.fontName, 'normal');
    this.doc.setTextColor(0, 0, 0);
    this.doc.text('Date: ' + today.toLocaleString('en-US', { hour12: false }), this.xStartPos, y);
    this.doc.text('EmpireCLS Worldwide Chauffeured Services', this.doc.internal.pageSize.width / 2, y, { align: 'center' });
    this.doc.text('Page No. ' + String(this.pageCount++), this.docWidth - 10, y, { align: 'right' });
    y += 3;
    this.doc.line(this.xStartPos, y, this.docWidth - this.xStartPos, y, 'FD');

    y += 5;
    this.doc.setFontSize(10).setFont(this.fontName, 'bold');
    this.doc.setTextColor(0, 0, 204);
    this.doc.text('PAY STATEMENT', this.doc.internal.pageSize.width / 2, y, { align: 'center' });
    if (!payrollPeriod.payrollPeriodLocked && !finalVersion) {
      this.doc.setFontSize(16).setFont(this.fontName, 'normal');
      this.doc.setTextColor(128, 0, 0);
      this.doc.text('!!! PRELIM !!!', this.xStartPos, y + 1, { align: 'left' });
      this.doc.text('!!! PRELIM !!!', this.docWidth - 8, y + 1, { align: 'right' });
    }
    this.doc.setTextColor(0, 0, 0);
    y += 3;
    this.doc.line(this.xStartPos, y, this.docWidth - this.xStartPos, y);

    this.doc.setFontSize(10).setFont(this.fontName, 'normal');
    y += 5;
    this.doc.text(h?.driverName, this.xStartPos, y);
    this.doc.text('DRIVER:', 100, y);
    this.doc.text(h?.driverNumber, 138, y, { align: 'right' });
    this.doc.text('CHECK DATE:' + (payrollPeriod.payrollCheckDate ? moment(payrollPeriod.payrollCheckDate).format('MM/DD/YYYY') : formatDate(today, 'MM/dd/yyyy', 'en-US')), 140, y);
    y += this.lineSpace;
    this.doc.text(h?.driverAddress1 + ' ' + h?.driverAddress2, this.xStartPos, y);
    this.doc.text('PAY PERIOD: ' + formatDate(payrollPeriod.startDate, 'MM/dd/yyyy', 'en-US') + '  TO  ' + formatDate(payrollPeriod.endDate, 'MM/dd/yyyy', 'en-US'), 100, y);
    y += this.lineSpace;
    this.doc.text(h?.driverCity + ', ' + h?.driverStateAbbr + ' ' + h?.driverPostalCode, this.xStartPos, y);

    y += 3;

    this.doc.setFillColor(192, 192, 192);
    this.doc.rect(this.xStartPos - 1, y, this.docWidth - ((this.xStartPos - 1) * 2), 12, 'F');

    this.doc.setFontSize(10).setFont(this.fontName, 'bold');
    y += 5;
    this.doc.text('DESCRIPTION', this.columnXPos[1], y);
    this.doc.text('TRIP', this.columnXPos[4], y);
    this.doc.text('TRIP', this.columnXPos[5], y);
    this.doc.text('TRIP', this.columnXPos[6], y);
    y += this.lineSpace;
    this.doc.text('DATE', this.columnXPos[0], y);
    this.doc.text('TRIP #', this.columnXPos[1], y);
    this.doc.text('PASSENGER', this.columnXPos[2], y);
    this.doc.text('DURATION', this.columnXPos[4], y);
    this.doc.text('START', this.columnXPos[5], y);
    this.doc.text('END', this.columnXPos[6], y);
    this.doc.text('EXPS', this.columnXPos[7], y, { align: 'right' });
    this.doc.text('GRATS', this.columnXPos[8], y, { align: 'right' });

    y += 3;
    //this.doc.setLineDashPattern([2, 1], 0);
    //this.doc.setDrawColor(255, 0, 0);
    //this.doc.line(this.xStartPos, y, this.docWidth - this.xStartPos, y, 'FD');

    this.doc.setFontSize(10).setFont(this.fontName, 'normal');
    this.doc.setTextColor(0, 0, 0);
    return y + this.lineSpace + 1;
  }

  addActivityLineToPDFReport(today: Date, a: IPayrollDayActivity, y: number, h: IPayrollDayHeader, payrollPeriod: IPayrollPeriod, finalVersion: boolean): number {

    if (a.recordType == PayrollRecordType.warnings || (a.recordType == PayrollRecordType.reportingTime && payrollPeriod.versionNumber >= 5 && payrollPeriod.payrollLocation == 'LAX')) 
      return 0;
    var date = a.recordType == PayrollRecordType.reportingTime || a.recordType == PayrollRecordType.startShift
      || a.recordType == PayrollRecordType.shiftBreakStart || a.recordType == PayrollRecordType.manualShiftBreakStart
      ? formatDate(a.startDateTime, 'MM/dd/yyyy', 'en-US')
      : a.recordType == PayrollRecordType.endOfShift || a.recordType == PayrollRecordType.shiftBreakEnd || a.recordType == PayrollRecordType.manualShiftBreakEnd
        ? formatDate(a.endDateTime, 'MM/dd/yyyy', 'en-US')
        : '';

    var time = a.recordType == PayrollRecordType.startShift
      || a.recordType == PayrollRecordType.shiftBreakStart
      || a.recordType == PayrollRecordType.manualShiftBreakStart
      || (a.recordType == PayrollRecordType.reportingTime /*&& payrollPeriod.versionNumber == 1*/)
      ? formatDate(a.startDateTime, 'HH:mm', 'en-US')
      : a.recordType == PayrollRecordType.endOfShift || a.recordType == PayrollRecordType.shiftBreakEnd
        || a.recordType == PayrollRecordType.manualShiftBreakEnd
        ? formatDate(a.endDateTime, 'HH:mm', 'en-US')
        : '';

    var actionDesc = a.recordType == PayrollRecordType.trip || a.recordType == PayrollRecordType.standBy
      ? a.confirmationNumber : a.recordType == PayrollRecordType.adjustmen ? '' : a.description;

    var pax = a.recordType == PayrollRecordType.trip || a.recordType == PayrollRecordType.standBy
      ? a.description
      : a.recordType == PayrollRecordType.adjustmen
        ? this.getAdjustmentsDescription(a.description)
        : '';

    var duration: string = a.recordType == PayrollRecordType.trip || a.recordType == PayrollRecordType.standBy
      || a.recordType == PayrollRecordType.reportingTimeAdjustment || a.recordType == PayrollRecordType.mealBreakTime
      //|| (a.recordType == PayrollRecordType.reportingTime && payrollPeriod.versionNumber > 1)
      ? this.minutesToTime(a.tripDurationMinutes) : '';

    var startTime: string = '';
    var endTime: string = '';
    var grat: string = '';
    var exp: string = '';
    if (a.recordType == PayrollRecordType.trip || a.recordType == PayrollRecordType.standBy || a.recordType == PayrollRecordType.mealBreakRecord) {
      startTime = formatDate(a.startDateTime, 'HH:mm', 'en-US');
      endTime = formatDate(a.endDateTime, 'HH:mm', 'en-US');
      if (a.recordType != PayrollRecordType.mealBreakRecord) {
        grat = String(Number(a.gratuity).toFixed(2));
        exp = String(Number(a.miscExpenses).toFixed(2));
      }
    }

    if (a.recordType == PayrollRecordType.adjustmen) {
      grat = a.description == 'gratuity' ? String(Number(a.gratuity).toFixed(2)) : '';
      exp = a.description != 'gratuity' ? String(Number(a.miscExpenses).toFixed(2)) : '';
    }

    var addlPos = a.recordType == PayrollRecordType.trip || a.recordType == PayrollRecordType.standBy ? 3 : 0;

    this.doc.setFontSize(10).setFont(this.fontName, 'normal');

    this.doc.text(date, this.columnXPos[0], y);
    this.doc.text(actionDesc, this.columnXPos[1] + addlPos, y);
    this.doc.text(pax, this.columnXPos[2], y);
    this.doc.text(time, this.columnXPos[3], y);
    this.doc.text(duration, this.columnXPos[4], y);
    this.doc.text(startTime, this.columnXPos[5], y);
    this.doc.text(endTime, this.columnXPos[6], y);
    this.doc.text(exp, this.columnXPos[7], y, { align: 'right' });
    this.doc.text(grat, this.columnXPos[8], y, { align: 'right' });

    y = this.lineSpace;
    y = this.testForEndOfPage(today, h, y, payrollPeriod, finalVersion);

    return y;
  }

  addAdjustmentLineToPDFReport(today: Date, a: IAdjustmentData, y: number, h: IPayrollDayHeader, payrollPeriod: IPayrollPeriod, finalVersion: boolean): number {

    this.doc.text(this.setAdjustmentsTitle(a), this.columnXPos[1], y);
    this.doc.text(this.findAdditionalMinutesReason(a.code).length ? this.minutesToTime(a.intValue) : '', this.columnXPos[3], y);
    this.doc.text(this.findAdditionalChargeReason(a.code).length ? a.decimalValue.toLocaleString('en-US', { minimumFractionDigits: 2 }) : '', this.columnXPos[7], y, { align: 'right' });

    y = this.lineSpace;
    y = this.testForEndOfPage(today, h, y, payrollPeriod, finalVersion);

    return y;
  }

  addUnengagedLine(today: Date, h: IPayrollDayHeader, yPos: number, unengagedMinutes: number, payrollPeriod: IPayrollPeriod, finalVersion: boolean): number {

    this.doc.text('Unengaged', this.columnXPos[1], yPos);
    this.doc.text(this.minutesToTime(unengagedMinutes), this.columnXPos[4], yPos);

    yPos = this.lineSpace;
    yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);

    return yPos;
  }

  addMealBreakLine(today: Date, h: IPayrollDayHeader, yPos: number, unpaidBreakMinutes: number, payrollPeriod: IPayrollPeriod, finalVersion: boolean): number {

    this.doc.text('Meal break time (-)', this.columnXPos[1], yPos);
    this.doc.text(this.minutesToTime(unpaidBreakMinutes), this.columnXPos[4], yPos);

    yPos = this.lineSpace;
    yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);

    return yPos;
  }

  addPenaltyBreakLine(today: Date, h: IPayrollDayHeader, yPos: number, penaltyBreakMinutes: number, payrollPeriod: IPayrollPeriod, finalVersion: boolean): number {

    if (payrollPeriod.versionNumber >= 5 && payrollPeriod.payrollLocation == 'LAX')
      this.doc.text('Meal Premium', this.columnXPos[1], yPos);
    else
      this.doc.text('Penalty meal break time', this.columnXPos[1], yPos);
    this.doc.text(this.minutesToTime(penaltyBreakMinutes), this.columnXPos[4], yPos);

    yPos = this.lineSpace;
    yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);

    return yPos;
  }

  addRestPenaltyBreakLine(today: Date, h: IPayrollDayHeader, yPos: number, penaltyBreakMinutes: number, payrollPeriod: IPayrollPeriod, finalVersion: boolean): number {

    this.doc.text('Rest Premium', this.columnXPos[1], yPos);
    this.doc.text(this.minutesToTime(penaltyBreakMinutes), this.columnXPos[4], yPos);

    yPos = this.lineSpace;
    yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);

    return yPos;
  }

  addDayTotals(today: Date, h: IPayrollDayHeader, t: IPayrollDayTotal, yPos: number, payrollPeriod: IPayrollPeriod, finalVersion: boolean): number {

    //this.doc.setLineDashPattern([2, 1], 0);
    this.doc.setLineWidth(0.1);
    this.doc.line(this.columnXPos[1], (yPos - this.lineSpace) + 3, this.columnXPos[1] + 62, (yPos - this.lineSpace) + 3, 'FD');
    this.doc.line(this.columnXPos[3] - 3, (yPos - this.lineSpace) + 3, this.columnXPos[3] + 12, (yPos - this.lineSpace) + 3, 'FD');
    this.doc.line(this.columnXPos[4], (yPos - this.lineSpace) + 3, this.columnXPos[4] + 10, (yPos - this.lineSpace) + 3, 'FD');
    this.doc.line(this.columnXPos[5] - 2, (yPos - this.lineSpace) + 3, this.columnXPos[5] + 12, (yPos - this.lineSpace) + 3, 'FD');
    this.doc.line(this.columnXPos[6] - 2, (yPos - this.lineSpace) + 3, this.columnXPos[6] + 10, (yPos - this.lineSpace) + 3, 'FD');
    this.doc.line(this.columnXPos[7] - 11, (yPos - this.lineSpace) + 3, this.columnXPos[7] + 2, (yPos - this.lineSpace) + 3, 'FD');
    this.doc.line(this.columnXPos[8] - 11, (yPos - this.lineSpace) + 3, this.columnXPos[8] + 2, (yPos - this.lineSpace) + 3, 'FD');
    yPos += this.lineSpace;
    yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);

    this.doc.setFontSize(10).setFont(this.fontName, 'bold');

    // regular
    this.doc.text('Regular:', 85, yPos, { align: 'right' });
    this.doc.text(this.minutesToTime(t.regularMinutes), this.columnXPos[3], yPos);
    yPos += this.lineSpace;
    yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);
    this.doc.setFontSize(10).setFont(this.fontName, 'bold');

    // Reporting
    if (payrollPeriod.payrollLocation == 'LAX' && !payrollPeriod.includeReportingInCalculation && payrollPeriod.versionNumber < 5) {
      this.doc.text('Reporting:', 85, yPos, { align: 'right' });
      this.doc.text(this.minutesToTime(t.reportingMinutes), this.columnXPos[3], yPos);
      yPos += this.lineSpace;
      yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);
      this.doc.setFontSize(10).setFont(this.fontName, 'bold');
    }
    if (payrollPeriod.payrollLocation == 'LAX' && t.penaltyBreakMinutes > 0) {
      this.doc.text(payrollPeriod.versionNumber < 5 ? 'Penalty Meal Break Time:' : 'Meal Premium', 85, yPos, { align: 'right' });
      this.doc.text(this.minutesToTime(t.penaltyBreakMinutes), this.columnXPos[3], yPos);
      yPos += this.lineSpace;
      yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);
      this.doc.setFontSize(10).setFont(this.fontName, 'bold');
    }
    if (payrollPeriod.payrollLocation == 'LAX' && t.restPenaltyBreakMinutes > 0 && payrollPeriod.versionNumber >= 5) {
      this.doc.text('Rest Premium', 85, yPos, { align: 'right' });
      this.doc.text(this.minutesToTime(t.penaltyBreakMinutes), this.columnXPos[3], yPos);
      yPos += this.lineSpace;
      yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);
      this.doc.setFontSize(10).setFont(this.fontName, 'bold');
    }

    // overtime
    this.doc.text('Overtime:', 85, yPos, { align: 'right' });
    this.doc.text(this.minutesToTime(t.overtimeMinutes), this.columnXPos[3], yPos);
    yPos += this.lineSpace;
    yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);
    this.doc.setFontSize(10).setFont(this.fontName, 'bold');

    // doubletime
    if (payrollPeriod.payrollLocation == 'LAX') {
      this.doc.text('Double time:', 85, yPos, { align: 'right' });
      this.doc.text(this.minutesToTime(t.doubleTimeMinutes), this.columnXPos[3], yPos);
      yPos += this.lineSpace;
      yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);
      this.doc.setFontSize(10).setFont(this.fontName, 'bold');
    }
    this.doc.setFontSize(10).setFont(this.fontName, 'normal');

    this.doc.line(this.columnXPos[1], yPos, this.docWidth - this.xStartPos, yPos, 'FD');
    yPos += this.lineSpace;
    yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);

    this.doc.setFontSize(10).setFont(this.fontName, 'bold');

    this.doc.text('DAY SUB TOTAL:', this.columnXPos[1], yPos);
    this.doc.text(this.minutesToTime(t.totalDayMinutes), this.columnXPos[3], yPos);
    this.doc.text(String(Number(t.miscExpenses).toFixed(2)), this.columnXPos[7], yPos, { align: 'right' });
    this.doc.text(String(Number(t.gratuity).toFixed(2)), this.columnXPos[8], yPos, { align: 'right' });


    if (payrollPeriod.payrollLocation != 'LAX') {
      yPos += this.lineSpace * 2;
      yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);

      this.doc.setFontSize(10).setFont(this.fontName, 'bold');
      this.doc.text('Totals', this.columnXPos[3], yPos);
      this.doc.text('Regular', this.columnXPos[4], yPos);
      this.doc.text('Overtime', this.columnXPos[5], yPos);

      yPos += 4;
      yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);
      this.doc.setFontSize(10).setFont(this.fontName, 'bold');

      this.doc.line(this.columnXPos[3] - 1, (yPos - this.lineSpace) + 3, this.columnXPos[3] + 12, (yPos - this.lineSpace) + 3, 'FD');
      this.doc.line(this.columnXPos[4] - 1, (yPos - this.lineSpace) + 3, this.columnXPos[4] + 12, (yPos - this.lineSpace) + 3, 'FD');
      this.doc.line(this.columnXPos[5] - 1, (yPos - this.lineSpace) + 3, this.columnXPos[5] + 15, (yPos - this.lineSpace) + 3, 'FD');

      yPos += 2;
      yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);
      this.doc.setFontSize(10).setFont(this.fontName, 'bold');

      this.doc.text('Engaged', this.columnXPos[1], yPos);
      this.doc.text(this.minutesToTime(t.actualEngagedMinutes), this.columnXPos[3], yPos);
      this.doc.text(this.minutesToTime(t.engagedRegularMinutes), this.columnXPos[4], yPos);
      this.doc.text(this.minutesToTime(t.engagedOvertimeMinutes), this.columnXPos[5], yPos);

      yPos += this.lineSpace;
      yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);
      this.doc.setFontSize(10).setFont(this.fontName, 'bold');

      this.doc.text('Other', this.columnXPos[1], yPos);
      this.doc.text(this.minutesToTime(t.otherMinutes), this.columnXPos[3], yPos);
      this.doc.text(this.minutesToTime(t.otherRegularMinutes), this.columnXPos[4], yPos);
      this.doc.text(this.minutesToTime(t.otherOvertimeMinutes), this.columnXPos[5], yPos);

    }

    yPos += this.lineSpace * 4;
    yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);

    this.doc.setFontSize(10).setFont(this.fontName, 'normal');
    this.doc.setTextColor(0, 0, 0);
    return yPos;
  }

  addPeriodTotals(today: Date, h: IPayrollDayHeader, pd: IPayrollDayDetail[], yPos: number, payrollPeriod: IPayrollPeriod, finalVersion: boolean): number {

    this.doc.setFontSize(10).setFont(this.fontName, 'normal');
    h.totalAdjustment.adjustments.forEach(t => {
      if ((t.decimalValue ?? 0) != 0) {
        this.doc.text(this.setAdditionalChargeReasonTitle(t), this.columnXPos[2], yPos, { align: 'left' });
        this.doc.text(String(t.decimalValue.toFixed(2)), this.columnXPos[7], yPos, { align: 'right' });
        yPos += this.lineSpace;
        yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);
      }
    });
    this.doc.line(this.xStartPos, yPos, this.docWidth - this.xStartPos, yPos);
    yPos += this.lineSpace;
    yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);

    this.doc.setTextColor(0, 0, 204);
    this.doc.setFontSize(10).setFont(this.fontName, 'bold'); 

    var periodTotal: number = 0;
    var grat: number = 0;
    var exp: number = 0;
    var reg: number = 0;
    var rep: number = 0;
    var ovt: number = 0;
    var dbl: number = 0;
    var pen: number = 0;
    var eng: number = 0;
    var reng: number = 0;
    var oeng: number = 0;
    var oth: number = 0;
    var roth: number = 0;
    var ooth: number = 0;
    var rpen: number = 0;
    pd.forEach(p => {
      periodTotal += p.totals.totalDayMinutes;
      grat += p.totals.gratuity;
      exp += p.totals.miscExpenses;
      reg += p.totals.regularMinutes;
      rep += p.totals.reportingMinutes;
      ovt += p.totals.overtimeMinutes;
      dbl += p.totals.doubleTimeMinutes;
      pen += p.totals.penaltyBreakMinutes;
      eng += p.totals.actualEngagedMinutes;
      reng += p.totals.engagedRegularMinutes;
      oeng += p.totals.engagedOvertimeMinutes;
      oth += p.totals.otherMinutes;
      roth += p.totals.otherRegularMinutes;
      ooth += p.totals.otherOvertimeMinutes;
      rpen += p.totals.restPenaltyBreakMinutes;
    });

    this.doc.setFontSize(10).setFont(this.fontName, 'normal');
    h.totalAdjustment.adjustments.forEach(t => {
      if ((t.decimalValue ?? 0) != 0) {
        exp += t.decimalValue;
      }
    });

    this.doc.setFontSize(10).setFont(this.fontName, 'bold');

    // regular
    this.doc.text('Regular:', 85, yPos, { align: 'right' });
    this.doc.text(this.minutesToTime(reg), this.columnXPos[3], yPos);
    yPos += this.lineSpace;
    yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);
    this.doc.setTextColor(0, 0, 204);
    this.doc.setFontSize(10).setFont(this.fontName, 'bold');

    // Reporting
    if (payrollPeriod.payrollLocation == 'LAX' && !payrollPeriod.includeReportingInCalculation && payrollPeriod.versionNumber < 5) {
      this.doc.text('Reporting:', 85, yPos, { align: 'right' });
      this.doc.text(this.minutesToTime(rep), this.columnXPos[3], yPos);
      yPos += this.lineSpace;
      yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);
      this.doc.setTextColor(0, 0, 204);
      this.doc.setFontSize(10).setFont(this.fontName, 'bold');
    }
    if (payrollPeriod.payrollLocation == 'LAX') {
      this.doc.text(payrollPeriod.versionNumber < 5 ? 'Penalty Meal Break Time:' : 'Meal Premium', 85, yPos, { align: 'right' });
      this.doc.text(this.minutesToTime(pen), this.columnXPos[3], yPos);
      yPos += this.lineSpace;
      yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);
      this.doc.setTextColor(0, 0, 204);
      this.doc.setFontSize(10).setFont(this.fontName, 'bold');
    }
    if (payrollPeriod.payrollLocation == 'LAX' && payrollPeriod.versionNumber >= 5) {
      this.doc.text('Rest Premium', 85, yPos, { align: 'right' });
      this.doc.text(this.minutesToTime(rpen), this.columnXPos[3], yPos);
      yPos += this.lineSpace;
      yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);
      this.doc.setTextColor(0, 0, 204);
      this.doc.setFontSize(10).setFont(this.fontName, 'bold');
    }

    // overtime
    this.doc.text('Overtime:', 85, yPos, { align: 'right' });
    this.doc.text(this.minutesToTime(ovt), this.columnXPos[3], yPos);
    yPos += this.lineSpace;
    yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);
    this.doc.setFontSize(10).setFont(this.fontName, 'bold');
    this.doc.setTextColor(0, 0, 204);

    // doubletime
    if (payrollPeriod.payrollLocation == 'LAX') {
      this.doc.text('Double time:', 85, yPos, { align: 'right' });
      this.doc.text(this.minutesToTime(dbl), this.columnXPos[3], yPos);
      yPos += this.lineSpace;
      yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);
      this.doc.setFontSize(10).setFont(this.fontName, 'bold');
      this.doc.setTextColor(0, 0, 204);
    }
    this.doc.setFontSize(10).setFont(this.fontName, 'normal');

    this.doc.line(this.xStartPos, yPos - 2, this.docWidth - this.xStartPos, yPos - 2);
    yPos += this.lineSpace - 2;
    yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);
    this.doc.setFontSize(10).setFont(this.fontName, 'bold');
    this.doc.setTextColor(0, 0, 204);

    this.doc.setTextColor(153, 0, 0);
    this.doc.text('** PERIOD TOTAL **', this.columnXPos[1], yPos);
    this.doc.text(this.minutesToTime(periodTotal), this.columnXPos[3], yPos);
    this.doc.text(String(Number(exp).toFixed(2)), this.columnXPos[7], yPos, { align: 'right' });
    this.doc.text(String(Number(grat).toFixed(2)), this.columnXPos[8], yPos, { align: 'right' });


    if (payrollPeriod.payrollLocation != 'LAX') {

      this.doc.setFontSize(10).setFont(this.fontName, 'bold');
      this.doc.setTextColor(0, 0, 0);

      yPos += this.lineSpace * 2;
      yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);

      this.doc.setFontSize(10).setFont(this.fontName, 'bold');
      this.doc.text('Totals', this.columnXPos[3], yPos);
      this.doc.text('Regular', this.columnXPos[4], yPos);
      this.doc.text('Overtime', this.columnXPos[5], yPos);

      yPos += 4;
      yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);

      this.doc.line(this.columnXPos[3] - 1, (yPos - this.lineSpace) + 3, this.columnXPos[3] + 12, (yPos - this.lineSpace) + 3, 'FD');
      this.doc.line(this.columnXPos[4] - 1, (yPos - this.lineSpace) + 3, this.columnXPos[4] + 12, (yPos - this.lineSpace) + 3, 'FD');
      this.doc.line(this.columnXPos[5] - 1, (yPos - this.lineSpace) + 3, this.columnXPos[5] + 15, (yPos - this.lineSpace) + 3, 'FD');

      yPos += 3;
      yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);


      this.doc.text('Engaged', this.columnXPos[1], yPos);
      this.doc.text(this.minutesToTime(eng), this.columnXPos[3], yPos);
      this.doc.text(this.minutesToTime(reng), this.columnXPos[4], yPos);
      this.doc.text(this.minutesToTime(oeng), this.columnXPos[5], yPos);

      yPos += this.lineSpace;
      yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);

      this.doc.text('Other', this.columnXPos[1], yPos);
      this.doc.text(this.minutesToTime(oth), this.columnXPos[3], yPos);
      this.doc.text(this.minutesToTime(roth), this.columnXPos[4], yPos);
      this.doc.text(this.minutesToTime(ooth), this.columnXPos[5], yPos);

      yPos += 4;
      yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);

      this.doc.line(this.columnXPos[3] - 1, (yPos - this.lineSpace) + 3, this.columnXPos[3] + 12, (yPos - this.lineSpace) + 3, 'FD');
      this.doc.line(this.columnXPos[4] - 1, (yPos - this.lineSpace) + 3, this.columnXPos[4] + 12, (yPos - this.lineSpace) + 3, 'FD');
      this.doc.line(this.columnXPos[5] - 1, (yPos - this.lineSpace) + 3, this.columnXPos[5] + 15, (yPos - this.lineSpace) + 3, 'FD');

      yPos += 3;
      yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);

      this.doc.text(this.minutesToTime(eng + oth), this.columnXPos[3], yPos);
      this.doc.text(this.minutesToTime(reng + roth), this.columnXPos[4], yPos);
      this.doc.text(this.minutesToTime(oeng + ooth), this.columnXPos[5], yPos);
    }

    yPos += this.lineSpace;
    yPos = this.testForEndOfPage(today, h, yPos, payrollPeriod, finalVersion);
    this.doc.setFontSize(10).setFont(this.fontName, 'normal');
    this.doc.setTextColor(0, 0, 0);
    return yPos;
  }

  testForEndOfPage(today: Date, h: IPayrollDayHeader, yPos: number, payrollPeriod: IPayrollPeriod, finalVersion: boolean): number {
    if (yPos + this.buttomMargin >= this.docHeight) {
      this.doc.addPage();
      yPos = this.generatePDFHeader(today, h, payrollPeriod, finalVersion);
    }
    return yPos;
  }

  createTimeSheetPage(header: IPayrollDayHeader, payrollDayDetail: IPayrollDayDetail[]) {
    this.doc.addPage();

    var weekDays: string[] = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
    var rows: string[][] = [];
    for (var i = 0; i < 7; i++) {
      rows.push([
        weekDays[i],
        this.dateArray[i],
        this.getStartDate(payrollDayDetail, this.dateArray[i]),
        this.isDriverOff(payrollDayDetail, this.dateArray[i]),
        this.isDriverOff(payrollDayDetail, this.dateArray[i]),
        this.isDriverOff(payrollDayDetail, this.dateArray[i]),
        this.isDriverOff(payrollDayDetail, this.dateArray[i]),
        this.getEndDate(payrollDayDetail, this.dateArray[i]),
        this.getTotalHours(payrollDayDetail, this.dateArray[i])
      ]);
    }


    autoTable(this.doc, {
      headStyles: { fillColor: [70, 130, 180], valign: 'middle' },
      alternateRowStyles: { fillColor: [240, 248, 255] },
      tableLineColor: [70, 130, 180],
      tableLineWidth: 0.1,
      theme: 'grid',
      styles: {
        minCellHeight: 12
      },
      columnStyles: {
        0: { cellWidth: 27, fillColor: [70, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold', valign: 'middle' },
        1: { valign: 'middle' },
        2: { valign: 'middle' },
        3: { valign: 'middle' },
        4: { valign: 'middle' },
        5: { valign: 'middle' },
        6: { valign: 'middle' },
        7: { valign: 'middle' },
        8: { valign: 'middle' }
      },
      margin: { left: 10, top: 50, right: 10 },
      head: [
        ['Day', 'Date', 'Day Start Time', 'Lunch Start', 'Lunch End', '2nd Lunch Start', '2nd Lunch End', 'Day End Time', 'Total Hours']
      ],
      body: rows,
      didDrawPage: (dataArg) => {
        this.doc.setFontSize(12).setFont(this.timeSheetFontColor, 'bold');
        this.doc.text('EmpireCLS Worldwide Chauffeured Services', 10, 10);

        this.doc.setFontSize(18).setFont(this.timeSheetFontColor, 'bold');
        this.doc.text('Time Sheet', 160, 10);

        this.doc.setFontSize(10).setFont(this.timeSheetFontColor, 'normal');
        this.doc.text(header?.driverName, 10, 20);
        this.doc.text(header?.driverAddress1 + ' ' + header?.driverAddress2, 10, 25);
        this.doc.text(header?.driverCity + ', ' + header?.driverStateAbbr + ' ' + header?.driverPostalCode, 10, 30);
        this.doc.text('Driver #: ' + header?.driverNumber, 10, 35);

        this.doc.setFontSize(8).setFont(this.timeSheetFontColor, 'normal');
        this.doc.text('Start of Day', 20, 160);
        this.doc.text('Start of day is 1/2 hour prior to your scheduled start time, or, 1/2 hour prior to your first', 60, 160);
        this.doc.text('job of the day, whichever is earlier. This will allow for all filling up of gas, car wash, etc...', 60, 163);

        this.doc.text('Lunch Break', 20, 170);
        this.doc.text('A 1/2 hour lunch break must be taken daily, either between scheduled jobs or during', 60, 170);
        this.doc.text('a time that is convenient, but does not impact service being rendered to an EmpireCLS client.', 60, 173);

        this.doc.setFontSize(8).setFont(this.timeSheetFontColor, 'bold');
        this.doc.text('All time sheets must be submitted to Human Resources no later than the FRIDAY of the non payroll week.', 60, 220);

        this.doc.setFontSize(8).setFont(this.timeSheetFontColor, 'normal');
        this.doc.setDrawColor(0, 0, 0);
        this.doc.setLineWidth(0.2);
        this.doc.line(80, 240, this.docWidth - 20, 240, 'FD');
        this.doc.text('Employee Signature', 80, 244);
        this.doc.text('Date', 170, 244);

        this.doc.line(80, 260, this.docWidth - 20, 260, 'FD');
        this.doc.text('Managers Signature', 80, 264);
        this.doc.text('Date', 170, 264);
      }
    });

    this.doc.setDrawColor(0, 0, 0);
  }

  getStartDate(payrollDayDetail: IPayrollDayDetail[], compDate: string): string {

    var ret = payrollDayDetail.find(p => formatDate(p.payrollDate, 'MM/dd/yyyy', 'en-US') == compDate);
    if (ret && ret.activities)
      return formatDate(ret.activities.find(a => a.recordType == PayrollRecordType.reportingTime || a.recordType == PayrollRecordType.startShift)
        .startDateTime, 'HH:mm', 'en-US');
    return 'OFF';
  }

  getEndDate(payrollDayDetail: IPayrollDayDetail[], compDate: string): string {

    var ret = payrollDayDetail.find(p => formatDate(p.payrollDate, 'MM/dd/yyyy', 'en-US') == compDate);

    if (ret)
      return formatDate(ret.activities.find(a => a.recordType == PayrollRecordType.endOfShift).endDateTime, 'HH:mm', 'en-US')
    return 'OFF';
  }

  getTotalHours(payrollDayDetail: IPayrollDayDetail[], compDate: string): string {

    var ret = payrollDayDetail.find(p => formatDate(p.payrollDate, 'MM/dd/yyyy', 'en-US') == compDate);

    if (ret)
      return this.minutesToTime(ret.totals.totalDayMinutes);
    return 'OFF';
  }

  isDriverOff(payrollDayDetail: IPayrollDayDetail[], compDate: string): string {

    var ret = payrollDayDetail.find(p => formatDate(p.payrollDate, 'MM/dd/yyyy', 'en-US') == compDate);
    if (ret)
      return '';
    return 'OFF';
  }

  convertRecordTypeToDescr(recordType: string): string {
    let descr = '';
    switch (recordType) {
      case '1': descr = 'Trip'; break
      case '2': descr = 'Stand By'; break;
      case '3': descr = 'Reporting Time'; break;
      case '4': descr = 'End Of Shift'; break;
      case '5': descr = 'Shift Break Start'; break;
      case '6': descr = 'Shift Break End'; break;
      case '7': descr = 'Meal Break Time (-)'; break;
      case '8': descr = 'Premium Meal'; break;
      case '9': descr = 'Reporting Time Adjustment'; break;
      case '10': descr = 'Unengaged'; break;
      case '11': descr = 'Start Shift'; break;
      case '12': descr = 'Courtesy Car Trip'; break;
      case '13': descr = 'Adjustmen'; break;
      case '14': descr = 'Manual Shift Break Start'; break;
      case '15': descr = 'Manual Shift Break End'; break;
      case '16': descr = 'Warnings'; break;
      case '17': descr = 'Premium Rest'; break;
      case '18': descr = 'Meal Break'; break;
    }
    return descr;
  }

  minutesToTime(min: number): string {
    var minToWork = min;
    if (min < 0) {
      minToWork = min * -1;
    }
    const minutes = minToWork % 60;
    const hours = Math.floor(minToWork / 60);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  dateRange(startDate: Date, endDate: Date): string[] {
    const dateArray: string[] = [];
    let currentDate = new Date(startDate);

    while (currentDate <= new Date(endDate)) {
      dateArray.push(formatDate(currentDate, 'MM/dd/yyyy', 'en-US'));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dateArray;
  }

  /////////////////////////////////////////////////////////////////////// END PDF /////////////////////////////////////////////////////////


  /////////////////////////////////////////////////////////////////////// EXCEL /////////////////////////////////////////////////////////

  preparePeriodTotalForExcelLAX(data: IPayrollDayData[], fileName: string, versionNumber: number, payrollLocation: string): IPrintData {

    var columnTitles: IPrintDataColumn[][] = [];

    columnTitles[0] = []; // Initialize the first row

    columnTitles[0].push({ columnName: 'Number', addColumnTotal: false }); 
    columnTitles[0].push({ columnName: 'Name', addColumnTotal: false }); 
    columnTitles[0].push({ columnName: 'Total Time', addColumnTotal: false });
    if (versionNumber >= 5 && payrollLocation == 'LAX') {
      columnTitles[0].push({ columnName: 'Meal Premium Time', addColumnTotal: false });
      columnTitles[0].push({ columnName: 'Rest Premium Time', addColumnTotal: false }); 
    } else {
      columnTitles[0].push({ columnName: 'Reporting Time', addColumnTotal: false });
      columnTitles[0].push({ columnName: 'Penalty Meal Break Time', addColumnTotal: false });
    }
    columnTitles[0].push({ columnName: 'Regular Time', addColumnTotal: false });
    columnTitles[0].push({ columnName: 'Overtime Time', addColumnTotal: false });
    columnTitles[0].push({ columnName: 'Double Time Time', addColumnTotal: false });
    columnTitles[0].push({ columnName: 'Total Hours', addColumnTotal: true, style: { numberFormat: "#,##0.00;" } });
    columnTitles[0].push({ columnName: 'Regular Hours', addColumnTotal: true, style: { numberFormat: "#,##0.00;" } });
    columnTitles[0].push({ columnName: 'Overtime Hours', addColumnTotal: true, style: { numberFormat: "#,##0.00;" } });
    columnTitles[0].push({ columnName: 'Double Hours', addColumnTotal: true, style: { numberFormat: "#,##0.00;" } });
    if (versionNumber >= 5 && payrollLocation == 'LAX') {
      columnTitles[0].push({ columnName: 'Meal Premium Hours', addColumnTotal: true, style: { numberFormat: "#,##0.00;" } });
      columnTitles[0].push({ columnName: 'Rest Premium Hours', addColumnTotal: true, style: { numberFormat: "#,##0.00;" } });
    } else {
      columnTitles[0].push({ columnName: 'Reporting Hours', addColumnTotal: true, style: { numberFormat: "#,##0.00;" } });
      columnTitles[0].push({ columnName: 'Penalty Meal Break Hours', addColumnTotal: true, style: { numberFormat: "#,##0.00;" } });
    }
    columnTitles[0].push({ columnName: 'Grat', addColumnTotal: true, style: { numberFormat: "#,##0.00;" } });
    columnTitles[0].push({ columnName: 'Exp', addColumnTotal: true, style: { numberFormat: "#,##0.00;(#,##0.00)" } });
    
    var printData: IPrintData = {
      pageTitle: fileName,
      rowsToFreeze: 1,
      columnsToFreeze: 2,
      sections: [
        {
          sectionTitle: 'Payroll Period Totals',
          columnTitles: columnTitles,
          rows: []
        }
      ]
    };

    console.log('data', data);

    var driverNumber: string = '';
    var payrollDayDetail: IPayrollDayDetail[] = [];
    var header: IPayrollDayHeader = {};
    var printLine: boolean = false;
    data.forEach((d, idx) => {
      if (driverNumber != d.header.driverNumber) {
        if (driverNumber.length > 0) {
          printData.sections[0].rows.push(this.putLaLineIntoExcel(header, payrollDayDetail, versionNumber, payrollLocation));
          printLine = false;
        }
        header = d.header;
        driverNumber = d.header.driverNumber;
        payrollDayDetail = d.payrollDaysData;
      }
      printLine = true;
    });

    if (printLine)
      printData.sections[0].rows.push(this.putLaLineIntoExcel(header, payrollDayDetail, versionNumber, payrollLocation));
    return printData;
  }

  putLaLineIntoExcel(header: IPayrollDayHeader, payrollDayDetail: IPayrollDayDetail[], versionNumber: number, payrollLocation: string): any[] {
    const roundTo = function (num: number, places: number) {
      const factor = 10 ** places;
      return Math.round(num * factor) / factor;
    };

    var row: any[] = [];

    var tot: number = 0;
    var rep: number = 0;
    var pen: number = 0;
    var rpen: number = 0;
    var reg: number = 0;
    var ovt: number = 0;
    var dbl: number = 0;
    var grat: number = 0;
    var exp: number = 0;
    payrollDayDetail.map(p => {
      tot += p.totals.totalDayMinutes;
      rep += p.totals.reportingMinutes;
      pen += p.totals.penaltyBreakMinutes;
      rpen += p.totals.restPenaltyBreakMinutes;
      reg += p.totals.regularMinutes;
      ovt += p.totals.overtimeMinutes;
      dbl += p.totals.doubleTimeMinutes;
      grat += p.totals.gratuity;
      exp += p.totals.miscExpenses;
    });

    header.totalAdjustment.adjustments.forEach(t => {
      exp += t.decimalValue ?? 0;
    });

    row.push(header.driverNumber);
    row.push(header.driverName);
    row.push(this.minutesToTime(tot));
    if (versionNumber < 5 || payrollLocation != 'LAX')
      row.push(this.minutesToTime(rep));
    row.push(this.minutesToTime(pen));
    if (versionNumber >= 5 && payrollLocation == 'LAX') 
      row.push(this.minutesToTime(rpen));
    row.push(this.minutesToTime(reg));
    row.push(this.minutesToTime(ovt));
    row.push(this.minutesToTime(dbl));

    row.push(roundTo(tot / 60.0, 2));
    row.push(roundTo(reg / 60.0, 2));
    row.push(roundTo(ovt / 60.0, 2));
    row.push(roundTo(dbl / 60.0, 2));
    if (versionNumber < 5 || payrollLocation != 'LAX')
      row.push(roundTo(rep / 60.0, 2));
    row.push(roundTo(pen / 60.0, 2));
    if (versionNumber >= 5 && payrollLocation == 'LAX')
      row.push(roundTo(rpen / 60.0, 2));

    row.push(grat);
    row.push(exp);

    return row;
  }

  preparePeriodTotalForExcel(data: IPayrollDayData[], fileName: string): IPrintData {

    const roundTo = function (num: number, places: number) {
      const factor = 10 ** places;
      return Math.round(num * factor) / factor;
    };

    var printData: IPrintData = {
      pageTitle: fileName,
      rowsToFreeze: 1,
      columnsToFreeze: 2,
      sections: [
        {
          sectionTitle: 'Payroll Period Totals',
          columnTitles: [
            [
              { columnName: 'Number', addColumnTotal: false },
              { columnName: 'Name', addColumnTotal: false },
              { columnName: 'Total Time', addColumnTotal: false },
              { columnName: 'Regular Time', addColumnTotal: false },
              { columnName: 'Overtime Time', addColumnTotal: false },
              { columnName: 'Total Hours', addColumnTotal: true, style: { numberFormat: "#,##0.00;" } },
              { columnName: 'Regular Hours', addColumnTotal: true, style: { numberFormat: "#,##0.00;" } },
              { columnName: 'Overtime Hours', addColumnTotal: true, style: { numberFormat: "#,##0.00;" } },
              { columnName: 'Grat', addColumnTotal: true, style: { numberFormat: "#,##0.00;" } },
              { columnName: 'Exp', addColumnTotal: true, style: { numberFormat: "#,##0.00;(#,##0.00)" } }
            ]
          ],
          rows: []
        }
      ]
    };

    var driverNumber: string = '';
    var payrollDayDetail: IPayrollDayDetail[] = [];
    var header: IPayrollDayHeader = {};
    var printLine: boolean = false;
    data.forEach((d, idx) => {
      if (driverNumber != d.header.driverNumber || idx == data.length - 1) {
        if (driverNumber.length > 0) {
          printData.sections[0].rows.push(this.putLineIntoExcel(header, payrollDayDetail));
          printLine = false;
        }
        header = d.header;
        driverNumber = d.header.driverNumber;
        payrollDayDetail = d.payrollDaysData;
      }
      printLine = true;
    });
    if (printLine)
      printData.sections[0].rows.push(this.putLineIntoExcel(header, payrollDayDetail));

    return printData;
  }

  putLineIntoExcel(header: IPayrollDayHeader, payrollDayDetail: IPayrollDayDetail[]): any[] {
    const roundTo = function (num: number, places: number) {
      const factor = 10 ** places;
      return Math.round(num * factor) / factor;
    };

    var row: any[] = [];

    var tot: number = 0;
    var reg: number = 0;
    var ovt: number = 0;
    var grat: number = 0;
    var exp: number = 0;
    payrollDayDetail.map(p => {
      tot += p.totals.totalDayMinutes;
      reg += p.totals.regularMinutes;
      ovt += p.totals.overtimeMinutes;
      grat += p.totals.gratuity;
      exp += p.totals.miscExpenses;
    });

    header.totalAdjustment.adjustments.forEach(t => {
      exp += t.decimalValue ?? 0;
    });

    row.push(header.driverNumber);
    row.push(header.driverName);
    row.push(this.minutesToTime(tot));
    row.push(this.minutesToTime(reg));
    row.push(this.minutesToTime(ovt));

    row.push(roundTo(tot / 60.0, 2));
    row.push(roundTo(reg / 60.0, 2));
    row.push(roundTo(ovt / 60.0, 2));

    row.push(grat);
    row.push(exp);

    return row;
  }

  /////////////////////////////////////////////////////////////////////// END EXCEL /////////////////////////////////////////////////////////

  setAdditionalMinutesReason(): IDisplaySelectViewModel {
    const ret: IDisplaySelectViewModel = {
      description: 'Additional Minutes Reason',
      options: [
        { label: 'Other', value: 'other-minutes', icon: '' },
        { label: 'Meal Premium', value: 'penalty-meal', icon: '' },
        { label: 'Rest Premium', value: 'penalty-rest', icon: '' },
        { label: 'Chauffeur Relations', value: 'chauffeur-relations', icon: '' },
        { label: 'Paperwork', value: 'paperwork', icon: '' },
        { label: 'Drug Test', value: 'drug-test', icon: '' }
      ]
    };
    return ret;
  }

  setAdditionalChargeReason(): IDisplaySelectViewModel {
    const ret: IDisplaySelectViewModel = {
      description: 'Additional Charge Reason',
      options: [
        { label: 'Other', value: 'other-charge', icon: '' },
        { label: 'Car Wash', value: 'car-wash', icon: '' },
        { label: 'Cigarettes', value: 'cigarettes', icon: '' },
        { label: 'Airport ACCESS FEE', value: 'airport-access-fee', icon: '' },
        { label: 'Bar Stock', value: 'bar-stock', icon: '' },
        { label: 'Beverage', value: 'beverage', icon: '' },
        { label: 'Bottle Opener', value: 'bottle-opener', icon: '' },
        { label: 'Champagne', value: 'champagne', icon: '' },
        { label: 'Candy', value: 'candy', icon: '' },
        { label: 'Cell Charger', value: 'cell-charger', icon: '' },
        { label: 'Cell Phone Adapter', value: 'cell-phone-adapter', icon: '' },
        { label: 'Chauffeur Meals', value: 'chauffeur-meals', icon: '' },
        { label: 'Coffee', value: 'coffee', icon: '' },
        { label: 'Cooler', value: 'cooler', icon: '' },
        { label: 'Creamer', value: 'creamer', icon: '' },
        { label: 'Dinner', value: 'dinner', icon: '' },
        { label: 'Dog Food/Amenities', value: 'dog-food-amenities', icon: '' },
        { label: 'Driver Hotel', value: 'driver-hotel', icon: '' },
        { label: 'Ear Pods', value: 'ear-pods', icon: '' },
        { label: 'Fed Ex Shipment', value: 'fed-ex-shipment', icon: '' },
        { label: 'Gift Basket', value: 'gift-basket', icon: '' },
        { label: 'Gift Cards', value: 'gift-cards', icon: '' },
        { label: 'Groceries', value: 'groceries', icon: '' },
        { label: 'Hotel/Parking', value: 'hotel-parking', icon: '' },
        { label: 'Ice', value: 'ice', icon: '' },
        { label: 'Juice', value: 'juice', icon: '' },
        { label: 'Lodging', value: 'lodging', icon: '' },
        { label: 'Luggage Carts', value: 'luggage-carts', icon: '' },
        { label: 'Magazine', value: 'magazine', icon: '' },
        { label: 'Market Items', value: 'market-items', icon: '' },
        { label: 'Meals', value: 'meals', icon: '' },
        { label: 'Medications', value: 'medications', icon: '' },
        { label: 'Misc Items', value: 'misc-items', icon: '' },
        { label: 'Out Of Pocket', value: 'out-of-pocket', icon: '' },
        { label: 'Snacks And Drinks', value: 'snacks-and-drinks', icon: '' },
        { label: 'Tea', value: 'tea', icon: '' },
        { label: 'Ups Shipping', value: 'ups-shipping', icon: '' }
      ]
    };
    return ret;
  }

  findAdditionalMinutesReason(code: string): string {
    const addlMinReasons: IDisplaySelectViewModel = this.setAdditionalMinutesReason();
    const amr = addlMinReasons.options.find(o => o.value == code);
    if (amr)
      return amr.label;
    return '';
  }

  findAdditionalChargeReason(code: string): string {
    const addlChargeReasons: IDisplaySelectViewModel = this.setAdditionalChargeReason();
    const amr = addlChargeReasons.options.find(o => o.value == code);
    if (amr)
      return amr.label;
    return '';
  }

  getAdjustmentsDescription(code: string): string {
    switch (code) {
      case 'gratuity': return 'Gratuity';
      case 'toll': return 'Tolls';
      case 'parking': return 'Parking';
    }

    const amr = this.findAdditionalMinutesReason(code);
    if (amr.length)
      return amr;

    const acr = this.findAdditionalChargeReason(code);
    if (acr.length)
      return acr;
    return code;
  }

  setAdjustmentsTitle(d: IAdjustmentData): string {
    if (d.code == 'other-minutes' && d.stringValue?.length > 0) {
      return d.intValue < 0 ? `${d.stringValue} (-)` : d.stringValue;
    }
    const amr = this.findAdditionalMinutesReason(d.code);
    if (amr.length) {
      return d.intValue < 0 ? `${amr} (-)` : amr;
    }

    if (d.code == 'other-charge' && d.stringValue?.length > 0) {
      return d.stringValue;
    }
    const acr = this.findAdditionalChargeReason(d.code);
    if (acr.length)
      return acr;

    switch(d.code) {
      case 'gratuity': return 'Gratuity';
      case 'toll': return 'Tolls';
      case 'parking': return 'Parking';
    }

    return d.code;
  }

  setAdditionalChargeReasonTitle(d: IAdjustmentData): string {
    switch(d.code) {
      case 'gratuity': return 'Gratuity';
      case 'toll': return 'Tolls';
      case 'parking': return 'Parking';
    }

    if (d.code == 'other-charge' && d.stringValue?.length > 0) {
      return d.stringValue;
    }
    const acr = this.findAdditionalChargeReason(d.code);
    if (acr.length)
      return acr;
    return d.code;
  }

  setAdditionalMinutesReasonTitle(d: IAdjustmentData): string {
    if (d.code == 'other-minutes' && d.stringValue?.length > 0) {
      return d.stringValue;
    }
    const amr = this.findAdditionalMinutesReason(d.code);
    if (amr.length)
      return amr;
    return d.code;
  }
}

/**
 * 
 */
export interface IPayrollPeriod {
  payrollPeriodId?: number;
  startDate?: Date;
  endDate?: Date;
  payrollLocation?: string;
  payrollPeriodLocked?: boolean;
  queueStatus?: number;
  queueStatusXml?: XMLDocument;
  payrollCheckDate?: string;
  adjustmentsCommited?: boolean;
  lockedByUserName?: string;
  lockedDateTime?: Date;
  totalDriversInPeriod?: number;
  statementCreated?: number;
  statementErrors?: number;
  statementEmailed?: number;
  statementEmailErrors?: number;
  payrollPeriodDriverId?: number;
  versionNumber?: number;
  includeReportingInCalculation?: boolean;
  isEmailStatementActive?: boolean;
  runCalculationSuggestion?: boolean;
  includePrepTimeInCalculation?: boolean;
}

/**
 * 
 */
export interface IStatementData {
  fileName: string;
  statement: string;
}

/**
 * 
 */
export interface IPayrollDayData {
  payrollLocation: string;
  payrollPeriodStartDateTime: Date;
  payrollPeriodEndtDateTime: Date;
  header: IPayrollDayHeader;
  payrollDaysData: IPayrollDayDetail[];
  adjustmentsCommited: boolean;
}

/**
 * 
 */
export interface IPayrollDayDetail {
  payrollPeriodDriverDayId?: number;
  payrollDate: Date;
  activities: IPayrollDayActivity[];
  totals: IPayrollDayTotal;
  activityAdjustment: IPayrollActivityAdjustment;
  dayAdjustment: IPayrollDayAdjustment;
  actions?: IActionModel[];
  listIcons?: { iconName: string; tooltip: string; size: string; color: string }[];
  timesheetDetails?: ITimesheetDetail;
  notPricedCount?: number;
}

export interface ITimesheetDetail {
  issuesCount: number;
  issues: { notPriced: boolean, dayTooLong: boolean };
  readyToSignDateTimeUtc: Date;
  signed: boolean;
  signedDateTimeUtc: Date;
  jsonString: string;
  driverBreakDay?: IDriverBreakDay;
}

/**
 * 
 */
export interface IPayrollActivityAdjustment {
  adjustments: IActivitieAdjustment[];
}

/**
 * 
 */
export interface IPayrollDayAdjustment {
  adjustments: IAdjustmentData[];
}

/**
 * 
 */
export interface PayrollTotalAdjustment {
  adjustments: IAdjustmentData[];
}

/**
 * 
 */
export interface IAdjustmentData {
  code: string;
  intValue: number;
  stringValue: string;
  boolValue: boolean;
  decimalValue: number;
  lastUserSubmitAdjustment: string;
}

/**
 * 
 */
export interface IActivitieAdjustment {
  segmentRowId: number;
  confirmationNumber: string;
  adjustmentsData: IAdjustmentData[];
}

/**
 * 
 */
export interface IPayrollDayHeader {
  driverNumber?: string;
  driverName?: string;
  driverAddress1?: string;
  driverAddress2?: string;
  driverCity?: string;
  driverStateAbbr?: string;
  driverPostalCode?: string;
  driverCountryCode?: string;
  payrollPeriodDriverId?: number;
  driverId?: number;
  driverEmails?: string;
  statementStatus?: number;
  statementEmailStatus?: number;
  totalAdjustment?: PayrollTotalAdjustment;
  isReviewCompleted?: boolean;
}

/**
 * 
 */
export interface IPayrollDayActivity {
  recordType: string;
  segmentRowId: number;
  confirmationNumber: string;
  segmentType: string;
  segmentStatus: string;
  startDateTime: Date;
  endDateTime: Date;
  airport: string;
  pickupInfo: string;
  dropOffInfo: string;
  description: string;
  onLocationDateTime: Date;
  enRouteDateTime: Date;
  actualStartDateTime: Date;
  actualEndDateTime: Date;
  startPortalMinutes: number;
  endPortalMinutes: number;
  tripDurationMinutes: number;
  gratuity: number;
  miscExpenses: number;
  chaufferGroup: string;
  department: number;
  customerCode?: string;
  customerName?: string;
  calculatedDropOffGarageMinutes?: number;
  calculatedPickupGarageMinutes?: number;
  cdlPrepMinutes?: number;
  origDropOffPortalMinutes?: number;
  origEndDateTime?: Date;
  origPickupPortalMinutes?: number;
  origStartDateTime?: Date;
  canMoveTrip?: boolean;
  canMoveTripDateTime?: Date;
  movedPickupDateTime?: Date;
  isReadyForBilling?: boolean;
}

/**
 * 
 */
export interface IPayrollDayTotal {
  standByCount: number;
  tripsCount: number;
  unpaidBreakMinutes: number;
  unpaidBreakCount: number;
  penaltyBreakMinutes: number;
  tripMinutes: number;
  unengagedMinutes: number;
  engagedMinutes: number;
  standByMinutes: number;
  courtesyCarMinutes: number;
  mealBreakMinutes: number;
  shiftBreakMinutes: number;
  totalDayMinutes: number;
  subTotalDayMinutes: number;
  regularMinutes: number;
  reportingMinutes: number;
  reportingMinutesAdjustment: number;
  overtimeMinutes: number;
  doubleTimeMinutes: number;
  gratuity: number;
  miscExpenses: number;
  engagedRegularMinutes: number;
  engagedOvertimeMinutes: number;
  otherMinutes: number;
  otherRegularMinutes: number;
  otherOvertimeMinutes: number;
  minutesBeforeOvertimeStartForDay: number;
  minutesBeforeOvertimeStartForWeek: number;
  minutesBeforeDoubleTimeStartForDay: number;
  minutesBeforeDoubleTimeStartForWeek: number;
  actualEngagedMinutes: number;
  restBreakMinutes,
  restPenaltyBreakMinutes
}

/**
 * 
 */
export enum PayrollRecordType {
  trip = '1',
  standBy = '2',
  reportingTime = '3',
  endOfShift = '4',
  shiftBreakStart = '5',
  shiftBreakEnd = '6',
  mealBreakTime = '7',
  penaltyMealBreakTime = '8',
  reportingTimeAdjustment = '9',
  unengaged = '10',
  startShift = '11',
  courtesyCarTrip = '12',
  adjustmen = '13',
  manualShiftBreakStart = '14',
  manualShiftBreakEnd = '15',
  warnings = '16',
  penaltyRestBreakTime = '17',
  mealBreakRecord = '18'
}

/**
 * 
 */
export enum PayrollAdjustmenType {
  gratuity = '1',
  toll = '2',
  parking = '3',
  chauffeurRelations = '4',
  paperwork = '5',
  drugTesting = '6'
}

/**
 * 
 */
export interface IPayrollDisplay {
  payrollPeriodId: number;
  payrollPeriodDriverId: number;
  driverCode: string;
  driverName: string;
  confirmations: string[];
  numberOfTrips: number;
  numberOfStandBy: number;
  numberOfWorkingDays: number;
  totalDayMinutes: number;
  adjustmentsCommited: boolean;
  numberOfAdjustments: number;
  editUsers: string;
  versionNumber: number;
  includeReportingInCalculation: boolean;
  statementEmailStatus: number;
  statementEmailError: string;
  statementStatus: number;
  statementError: string;
  warningCount: number;
  includePrepTimeInCalculation: boolean;
  notSignedTimeSheetsCount: number;
  timeSheetsIssueCount: number;
  pricedTripsCount: number;
  notPricedCount: number;
  isReviewCompleted: boolean;
}


